import { zodResolver } from "@hookform/resolvers/zod";
import { ErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { useInfrastructureEventWithoutInternalMessages } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/hooks/useInfrastructureEvent";
import { AffectedTrain } from "features/CenterContent/RoleContent/Vaktleder/types";
import { TrainInfoBatchForm } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoBatchForm";
import {
  SelectedTrainProps,
  TrainInfoBatchModalHeader,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoBatchModalHeader";
import {
  SingleTrainFormSchema,
  TrickFormHookGroupedTrainFormSchema,
  groupedTrainFormSchema,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { TrainInfoRequest } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/requestSchema";
import {
  TrainInfoProvider,
  useTrainInfoContext,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useTrainInfoContext";
import {
  formStateHasCustomField,
  getRepresentativeTrainIds,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/utils";
import { BatchIncidentHiddenInput } from "features/CenterContent/shared/operationalInformation/components/BatchIncidentHiddenInput";
import { useOperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import {
  TrainSeriesGrouping,
  buildTrainSeriesGroupForSingleIncident,
} from "features/History/groupTrainSeries";
import {
  batchTrainFormToRequestBody,
  createInitialBatchFormStateFromTrainSeries,
} from "features/VehicleSidebar/AffectedTrains/utils/utils";
import { LogLevel, log } from "logging/datadogBrowserLogs";
import { FC, useEffect, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  DropsStaticAlert,
  Skeleton,
  SkeletonText,
  Stack,
} from "shared/components/Spor";
import { StepActionModal } from "shared/components/StepActionModal";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { useFormVerbose } from "shared/form/useFormVerbose";

export type RequestBodyCreateBatch = {
  type: "create";
  body: TrainInfoRequest[];
};

type TrainInfoBatchProps = {
  onSubmit: (body: RequestBodyCreateBatch) => void;
  onClose: () => void;
  submitStatus: "error" | "pending" | "idle" | "success";
  title?: string;
  submitButtonLabel?: string;
};

type SeriesData = {
  formState: SingleTrainFormSchema;
  affectedTrains: AffectedTrain[];
};

export type BatchFormState = {
  [series: string]: SeriesData;
};

/**
 * Render the train info form modal for creating new train events in batch
 * @param selectedTrains one or more trains selected in the batch
 * @param updateSelectedTrains edit trains in batch
 * @param onSubmit callback to perform when the form is submitted. The form must be valid before this callback is called.
 * @param onClose callback to perform when closing the modal
 * @param submitStatus the status of the action being performed onSubmit. Typically, the status of
 *                     the create/edit mutation
 * @param title override the title
 * @param submitButtonLabel override the label on the submit button
 */

const TrainInfoBatchDialogContent: FC<
  TrainInfoBatchProps & SelectedTrainProps
> = ({
  selectedTrains,
  updateSelectedTrains,
  onSubmit,
  onClose,
  submitStatus,
  title,
  submitButtonLabel,
}) => {
  const { t } = useTranslation();
  const activeTrainGrouping = useMemo<TrainSeriesGrouping>(
    () => buildTrainSeriesGroupForSingleIncident(selectedTrains)!, // Only returns null if selectedTrains is empty, which cannot happen in practice here
    [selectedTrains],
  );

  const [activeTrainSeriesIndex, setActiveTrainSeriesIndex] = useState(0);
  const trainSeriesOfActiveGroup = Object.keys(activeTrainGrouping.trainSeries);
  const activeSeriesKey = trainSeriesOfActiveGroup[activeTrainSeriesIndex];
  const allTrainSeries = activeTrainGrouping.trainSeries;
  const activeTrainSeries = allTrainSeries[activeSeriesKey];

  // Necessary for useTrainRouteBatch
  const { setAllTrainIds } = useTrainInfoContext();
  useEffect(() => {
    const allRepresentativeTrainIds = getRepresentativeTrainIds(allTrainSeries);
    setAllTrainIds(allRepresentativeTrainIds);
  }, [allTrainSeries]);

  // Zod has excellent support for dynamic keys but hook-form does not so
  // I trick it into thinking the group key is a specific weird-looking string
  const activeSeriesKeyForFormHook = activeSeriesKey as `\${groupKey}`;

  const { data: opInfoTypes, status: opInfoTypesStatus } =
    useOperationalInformationTypes(t);

  const { data: _infraStructureEventData } =
    useInfrastructureEventWithoutInternalMessages([
      activeTrainGrouping.eventUuid,
    ]);
  const infrastructureEvent = _infraStructureEventData?.[0];

  // Actual type is GroupedTrainFormSchema but react-hook-form can't handle dynamic keys
  const formMethods = useFormVerbose<TrickFormHookGroupedTrainFormSchema>({
    resolver: zodResolver(groupedTrainFormSchema),
  });
  const { handleSubmit, getValues, reset, trigger, watch, getFieldState } =
    formMethods;

  // Populate batch state with data from infrastructure event
  useEffect(() => {
    const initialFormState = createInitialBatchFormStateFromTrainSeries(
      activeTrainGrouping.trainSeries,
      infrastructureEvent,
    );

    reset(initialFormState);
  }, [infrastructureEvent]);

  const activeFormData = watch(activeSeriesKeyForFormHook);

  const formGroupCount = Object.keys(getValues() ?? {}).length;

  const showForm =
    opInfoTypesStatus === "success" && formGroupCount && infrastructureEvent;

  // Submitting the form
  const onSubmitForm = handleSubmit((data) => {
    if (!formGroupCount) {
      log(LogLevel.error, `TrainInfoBatch`, `BatchFormState is missing`);
      throw new Error("Missing batch form state, this should not be possible.");
    }
    const requestBody = batchTrainFormToRequestBody(data);
    if (requestBody) {
      onSubmit({ type: "create", body: requestBody });
    }
  });

  return (
    <FormProvider {...formMethods}>
      <StepActionModal
        title={title ?? `${t("Opprett")} ${t("toghendelse")}`}
        actionTitle={submitButtonLabel ?? `${t("Opprett")} ${t("hendelse")}`}
        onClose={onClose}
        onSubmit={onSubmitForm}
        onSubmitDisabled={() => {
          void trigger(); // Generate detailed errors on current train series
        }}
        onStep={async () => {
          await trigger(`${activeSeriesKeyForFormHook}`);
          const fieldState = getFieldState(activeSeriesKeyForFormHook);
          return !fieldState.invalid;
        }}
        isSubmitDisabled={false}
        loading={submitStatus === "pending"}
        isSuccess={submitStatus === "success"}
        isError={submitStatus === "error"}
        successMessage={t("Hendelse opprettet")}
        failureMessage={`${t("Kunne ikke opprette hendelse.")} ${t("Prøv på nytt, eller kontakt IT hvis feilen vedvarer.")}`}
        activeStep={activeTrainSeriesIndex}
        setActiveStep={setActiveTrainSeriesIndex}
        withBackButton
        trainSeries={Object.keys(activeTrainGrouping.trainSeries)}
        size="lg"
      >
        <ErrorBoundary>
          <Box w="100%" display="grid" gap={4}>
            {opInfoTypesStatus === "pending" && (
              <>
                <Stack gap="12px">
                  <Skeleton height="36px" />
                  <Skeleton height="36px" />
                </Stack>
                <SkeletonText lineClamp="3" width="30%" />
              </>
            )}

            {opInfoTypesStatus === "error" && <FailureMessage />}

            <TrainInfoBatchModalHeader
              activeTrainSeriesKey={activeSeriesKeyForFormHook}
              activeTrainSeriesIndex={activeTrainSeriesIndex}
              setActiveTrainSeriesIndex={setActiveTrainSeriesIndex}
              activeTrainSeries={activeTrainSeries}
              activeTrainGrouping={activeTrainGrouping}
              selectedTrains={selectedTrains}
              updateSelectedTrains={updateSelectedTrains}
            />

            <BatchIncidentHiddenInput
              incidentId={activeTrainGrouping.incidentId}
              pathPrefix={activeSeriesKeyForFormHook}
              numberOfGroups={
                activeFormData?.length ?? activeTrainSeries.length
              }
            />

            {showForm && (
              <>
                <TrainInfoBatchForm
                  opInfoTypes={opInfoTypes}
                  infrastructureEvent={infrastructureEvent}
                  activeTrainSeriesKey={activeSeriesKeyForFormHook}
                  groupsInSeries={activeFormData.length}
                  mode="create"
                />

                {activeFormData.some((data) =>
                  formStateHasCustomField(data.trainForm),
                ) && (
                  <DropsStaticAlert variant="info" id="infoBoxActionModal">
                    {t(
                      "Hendelsen inneholder egendefinert tekst. Sjekk at oppsummeringen ser riktig ut før du går videre.",
                    )}
                  </DropsStaticAlert>
                )}
              </>
            )}

            {!showForm && <SkeletonLoader skeletonType="affectedTrains" />}
          </Box>
        </ErrorBoundary>
      </StepActionModal>
    </FormProvider>
  );
};

const TrainInfoBatchModal: FC<TrainInfoBatchProps & SelectedTrainProps> = (
  props,
) => (
  <TrainInfoProvider>
    <TrainInfoBatchDialogContent {...props} />
  </TrainInfoProvider>
);

export default TrainInfoBatchModal;
